import { useState } from "react";
import "../../Styles/exportToFigma.scss";
import { hexToHSL } from "../Colorize/ColorUtils";
import { CloseButton } from "../ReusableComponents/PopupDefault";
import { usePopupQueue } from '../ReusableComponents/usePopupQueue';

const ExportToFigma = ({ onClose, palette, colorName, selectedIndex, squareNames }) => {
  const { activePopups, addPopup } = usePopupQueue();
  const [isCopied, setIsCopied] = useState(false);

  // Format color name for groupName
  const colorNameFormatted = colorName.trim();

  const baseShade = selectedIndex !== null && selectedIndex !== undefined
    ? squareNames[selectedIndex]
    : "600";

  // Convert color to HSL object with h, s, l properties
  const formatHSLObject = (color) => {
    try {
      // Check if already HSL format
      if (typeof color === 'string' && color.startsWith('hsl(')) {
        // Parse the HSL string
        const hslMatch = color.match(/hsl\(\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*%\s*,\s*(\d+\.?\d*)\s*%\s*\)/);

        if (hslMatch) {
          const h = parseFloat(hslMatch[1]);
          const s = parseFloat(hslMatch[2]);
          const l = parseFloat(hslMatch[3]);

          return {
            h: Math.round(h),
            s: Math.round(s),
            l: Math.round(l)
          };
        }
      }

      // Convert hex to HSL
      const { h, s, l } = hexToHSL(color);
      return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100)
      };
    } catch (e) {
      console.error('Invalid color value for HSL conversion:', color, e);
      return { h: 0, s: 0, l: 0 };
    }
  };

  // Generate the JSON output
  const generateFigmaJSON = () => {
    const output = {
      groupName: colorNameFormatted,
      baseShade: baseShade,
      variables: {
        "50": { hsl: formatHSLObject(palette[0]) },
        "100": { hsl: formatHSLObject(palette[1]) },
        "200": { hsl: formatHSLObject(palette[2]) },
        "300": { hsl: formatHSLObject(palette[3]) },
        "400": { hsl: formatHSLObject(palette[4]) },
        "500": { hsl: formatHSLObject(palette[5]) },
        "600": { hsl: formatHSLObject(palette[6]) },
        "700": { hsl: formatHSLObject(palette[7]) },
        "800": { hsl: formatHSLObject(palette[8]) },
        "900": { hsl: formatHSLObject(palette[9]) },
        "950": { hsl: formatHSLObject(palette[10]) }
      }
    };

    return JSON.stringify(output, null, 2);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generateFigmaJSON());
    addPopup('Copied JSON data');

    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  };

  return (
    <div className="export-to-figma-popup">


      <div className="intro">
        <h4>Export JSON</h4>
        <p>Copy and paste the JSON into the <a href="https://www.figma.com/community/plugin/1544422242387734389" target="_blank" rel="noopener noreferrer">Figma plugin</a>.</p>
      </div>

      <div className="code-block-wrap">
        <pre className="code-block">{generateFigmaJSON()}</pre>
      </div>
      


      <div className="btns">
        <button
          className={`copy-btn svg---text pri-btn ${isCopied ? 'copied' : ''}`}
          onClick={handleCopy}
        >
          <svg className="copy-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M3.7 12.7H2.8C2.32261 12.7 1.86477 12.5104 1.52721 12.1728C1.18964 11.8352 1 11.3774 1 10.9V2.8C1 2.32261 1.18964 1.86477 1.52721 1.52721C1.86477 1.18964 2.32261 1 2.8 1H10.9C11.3774 1 11.8352 1.18964 12.1728 1.52721C12.5104 1.86477 12.7 2.32261 12.7 2.8V3.7M9.1 7.3H17.2C18.1941 7.3 19 8.10589 19 9.1V17.2C19 18.1941 18.1941 19 17.2 19H9.1C8.10589 19 7.3 18.1941 7.3 17.2V9.1C7.3 8.10589 8.10589 7.3 9.1 7.3Z" stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <svg className="checkmark-icon" width={18} height={18} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#ffffff">
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
          <span>{isCopied ? 'Copied' : 'Copy JSON'}</span>
        </button>
        <a
          className={`figma-btn svg---text sec-btn`}
          href="https://www.figma.com/community/plugin/1544422242387734389"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none"><path fill="currentColor" fillRule="evenodd" d="M3 3.62C3 2.172 4.205 1 5.691 1h4.618C11.795 1 13 2.173 13 3.62a2.6 2.6 0 01-1.215 2.19A2.6 2.6 0 0113 8c0 1.447-1.205 2.62-2.691 2.62h-.059a2.723 2.723 0 01-1.81-.681v2.413C8.44 13.818 7.205 15 5.707 15 4.223 15 3 13.83 3 12.38a2.6 2.6 0 011.215-2.19A2.6 2.6 0 013 8a2.6 2.6 0 011.215-2.19A2.6 2.6 0 013 3.62zm4.56 2.619H5.69c-1 0-1.81.788-1.81 1.761 0 .97.805 1.755 1.799 1.761h1.879V6.24zM8.44 8c0 .973.811 1.761 1.81 1.761h.059c1 0 1.81-.788 1.81-1.76 0-.974-.81-1.762-1.81-1.762h-.059c-.999 0-1.81.788-1.81 1.761zm-2.749 2.62H5.68c-.994.005-1.798.792-1.798 1.76 0 .97.82 1.762 1.824 1.762 1.019 0 1.853-.805 1.853-1.79V10.62H5.691zm0-8.762H7.56v3.523H5.691c-1 0-1.81-.789-1.81-1.762 0-.972.81-1.761 1.81-1.761zm2.75 3.523V1.858h1.868c1 0 1.81.789 1.81 1.761 0 .973-.81 1.762-1.81 1.762H8.44z" clipRule="evenodd" />
          </svg>
          <span>Open Figma Plugin</span>
        </a>
      </div>







      <CloseButton onClose={onClose} />

      {/* popup */}
      {activePopups.length > 0 && (
        <div className="popup-timed-container centered">
          {activePopups.map((popup) => (
            <div key={popup.id} className="popup-timed">
              <div className="popup-timed-content">
                <div className="svg---text">
                  {/* <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg> */}
                  <svg className="check-icon-fill" width={24} height={24} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                  </svg>
                  <p>{popup.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExportToFigma;